import { c as _c } from "react/compiler-runtime";
import { Info, Lock, Timer, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import type { ReviewRow, ReviewValue } from "@/components/ui/transaction-review-contract";
import "./money.css";
export type { ReviewRow, ReviewValue };

/**
 * THE REVIEW BEFORE MONEY MOVES, in v2 — a ledger slip. The legacy
 * `components/ui/TransactionReview`'s contract, one for one: the same
 * `ReviewRow` values (value / estimate / pending / unavailable — an estimate
 * always says what it excludes, an absent figure is a dash and never a zero,
 * and its reason is visible text), a real `<dl>`, figures on the end edge and
 * their small print on its own full-width line, the irreversibility stated in
 * words. Solid, never glass: a financial decision is read here. The legacy
 * component keeps its face for the flows not yet migrated.
 */
export function TransactionReview(t0) {
  const $ = _c(13);
  const {
    title,
    rows,
    irreversibility,
    notices
  } = t0;
  let t1;
  if ($[0] !== rows) {
    t1 = rows.map(_temp);
    $[0] = rows;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl>{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== notices) {
    t3 = notices ? <div className="mn-review-notices">{notices}</div> : null;
    $[4] = notices;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== irreversibility) {
    t4 = irreversibility ? <p className="mn-review-warn"><TriangleAlert aria-hidden={true} /><span>{irreversibility}</span></p> : null;
    $[6] = irreversibility;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4 || $[11] !== title) {
    t5 = <section className="mn-review" aria-label={title}>{t2}{t3}{t4}</section>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = title;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function _temp(row) {
  return <div key={row.id} className="mn-review-row" data-review-row={row.id} data-emphasis={row.emphasis && row.emphasis !== "default" ? row.emphasis : undefined}><div className="mn-review-line"><dt>{row.label}</dt><dd><Figure value={row.value} /></dd></div><Note value={row.value} /></div>;
}
function Figure(t0) {
  const $ = _c(4);
  const {
    value
  } = t0;
  if (value.kind === "value" || value.kind === "estimate") {
    const t1 = value.technical || undefined;
    let t2;
    if ($[0] !== t1 || $[1] !== value.text) {
      t2 = <span className="mn-fig" data-technical={t1}>{value.text}</span>;
      $[0] = t1;
      $[1] = value.text;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="mn-fig" data-absent={true} aria-hidden={true}>—</span>;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function Note(t0) {
  const $ = _c(12);
  const {
    value
  } = t0;
  if (value.kind === "estimate") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <TriangleAlert aria-hidden={true} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== value.basis) {
      t2 = <p className="mn-note" data-tone="estimate">{t1}<span>{value.basis}</span></p>;
      $[1] = value.basis;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (value.kind === "value") {
    let t1;
    if ($[3] !== value.note) {
      t1 = value.note ? <p className="mn-note"><Info aria-hidden={true} /><span>{value.note}</span></p> : null;
      $[3] = value.note;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  const Icon = value.kind === "pending" ? Timer : Lock;
  let t1;
  if ($[5] !== Icon) {
    t1 = <Icon aria-hidden={true} />;
    $[5] = Icon;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== value.reason) {
    t2 = <span>{value.reason}</span>;
    $[7] = value.reason;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <p className="mn-note">{t1}{t2}</p>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}